import { DataGridProProps } from "@mui/x-data-grid-pro";
import { format } from "date-fns";
import {
  MODULE_LAYOUT_LABELS,
  MODULE_STATUS_COLORS,
  MODULE_STATUS_LABELS,
  MODULE_TYPE_LABELS,
} from "entities/modules/constants";
import {
  ModuleData,
  ModuleLayout,
  ModuleStatus,
  ModuleType,
  UpdateStatusParams,
} from "entities/modules/types";
import Chip from "shared/components/Chip";
import DropdownList from "shared/components/DropdownList";
import FlexCenter from "shared/components/Layout/components/FlexCenter";
import { DrawerHookResult } from "shared/hooks/useDrawerState";
import { COLOR_PRIMARY_MAIN } from "shared/theme/colors";
import { ArrowForwardIcon } from "shared/theme/icons/ArrowForwardIcon";

type ColumnsProps = {
  moduleDetailsDrawer: DrawerHookResult<ModuleData>;
  handleStatus: (values: UpdateStatusParams) => void;
  handleDeleteModuleTemplate: () => void;
  setSelectedModuleTemplate: (value: ModuleData) => void;
  readOnly: boolean;
};

export const getModulesColumns = ({
  moduleDetailsDrawer,
  handleStatus,
  handleDeleteModuleTemplate,
  setSelectedModuleTemplate,
  readOnly,
}: ColumnsProps): DataGridProProps["columns"] => {
  const renderActions = (moduleTemplate: ModuleData) => {
    if (readOnly) {
      return (
        <FlexCenter
          onClick={() => moduleDetailsDrawer.openDrawer(moduleTemplate)}
        >
          <ArrowForwardIcon color={COLOR_PRIMARY_MAIN} />
        </FlexCenter>
      );
    }

    const statusTitle =
      moduleTemplate.status === ModuleStatus.Active ? "Unpublish" : "Publish";

    return (
      <DropdownList
        items={[
          {
            title: "Edit",
            onClick: () => moduleDetailsDrawer.openDrawer(moduleTemplate),
          },
          {
            title: statusTitle,
            onClick: () => {
              handleStatus({
                moduleId: moduleTemplate.id,
                status:
                  moduleTemplate.status === ModuleStatus.Active
                    ? ModuleStatus.Draft
                    : ModuleStatus.Active,
              });
            },
          },
          {
            title: "Delete",
            style: "remove",
            onClick: () => {
              setSelectedModuleTemplate(moduleTemplate);
              handleDeleteModuleTemplate();
            },
          },
        ]}
      />
    );
  };

  return [
    {
      field: "name",
      headerName: "Module name",
      editable: false,
      sortable: true,
      flex: 1,
    },
    {
      field: "type",
      headerName: "Type",
      editable: false,
      sortable: true,
      flex: 1,
      renderCell: (params) => {
        return MODULE_TYPE_LABELS[params.value as ModuleType];
      },
    },
    {
      field: "layout",
      headerName: "Layout",
      editable: false,
      sortable: true,
      flex: 1,
      renderCell: (params) => {
        return params.value
          ? MODULE_LAYOUT_LABELS[params.value as ModuleLayout]
          : "N/A";
      },
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      editable: false,
      sortable: true,
      flex: 1,
      renderCell: (params) => {
        if (params.value) {
          return format(params.value, "dd MMM yyyy, HH:mm");
        }
      },
    },
    {
      field: "status",
      headerName: "Status",
      editable: false,
      sortable: true,
      flex: 0.5,
      renderCell: (params) => {
        const status = params.value as ModuleStatus;
        return (
          <Chip
            title={MODULE_STATUS_LABELS[status]}
            bgColor={MODULE_STATUS_COLORS[status]}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      editable: false,
      align: "right",
      headerAlign: "right",
      flex: 0.5,
      renderCell: (params) => renderActions(params.row),
    },
  ];
};

export const generateTextForDeletingPopup = (gamesCount: number) => {
  if ((gamesCount || 0) > 0) {
    return `This module contain ${gamesCount} games. Are you sure you want to delete this module?`;
  }
  return "Are you sure you want to delete this module?";
};
